/**
 * "Term — clue" lines, the input format shared by the crossword and matching tools.
 *
 * One pair per line. The first separator on the line splits it: a tab (a paste from a
 * spreadsheet), a vertical bar, a colon, an equals sign, an en or em dash, or a hyphen
 * with a space on each side. A bare hyphen does not split, so "self-esteem" and
 * "x-axis" survive as terms.
 */

const SEPARATOR = /\t|\s*\|\s*|\s*:\s*|\s*=\s*|\s*[–—]\s*|\s+-\s+/;
const LINE_BREAK = /\r?\n/;

export interface TermEntry {
  term: string;
  clue: string;
  /** 1-based line number in the input, for error messages. */
  line: number;
}

export interface ParsedTerms {
  entries: TermEntry[];
  /** Lines that had text but no separator, or an empty side. 1-based. */
  invalidLines: number[];
}

export function parseTermLines(text: string): ParsedTerms {
  const entries: TermEntry[] = [];
  const invalidLines: number[] = [];
  const lines = text.split(LINE_BREAK);
  for (const [index, raw] of lines.entries()) {
    const line = raw.trim();
    if (line.length === 0) {
      continue;
    }
    const match = SEPARATOR.exec(line);
    if (!match) {
      invalidLines.push(index + 1);
      continue;
    }
    const term = line.slice(0, match.index).trim();
    const clue = line.slice(match.index + match[0].length).trim();
    if (term.length === 0 || clue.length === 0) {
      invalidLines.push(index + 1);
      continue;
    }
    entries.push({ term, clue, line: index + 1 });
  }
  return { entries, invalidLines };
}

/** Non-empty trimmed lines — distractors, names and other one-per-line lists. */
export function parseLines(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
